import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Activity, Globe, KeyRound, Plus, Copy, Trash2, LogOut,
  TrendingUp, MessageSquare, Crown, Trophy, Zap, Loader2, Shield,
  Radio, Server, Gauge, DollarSign, Receipt, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ApiKeysSection } from "@/components/ApiKeysSection";
import { useAuth } from "@/contexts/AuthContext";
import {
  adminStats, adminUsers, adminListCodes, adminCreateCode, adminDeleteCode,
  adminToggleCode, adminLiveStats, adminRevenue,
} from "@/lib/api";

const COLORS = ["#C47A3A", "#D98E4B", "#8B5A2B", "#E5A06A"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!admin) {
      navigate("/adminayush/login");
      return;
    }
    loadAll();
  }, [admin, navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u, c] = await Promise.all([
        adminStats(),
        adminUsers(),
        adminListCodes(),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setCodes(c.data.codes || []);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
          <div className="flex items-center gap-3">
            <Logo />
            <Badge variant="outline" className="gap-1 border-primary/40 text-primary" data-testid="admin-badge">
              <Shield className="h-3 w-3" /> Admin
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="admin-logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-8 lg:py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Control Room</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Welcome back, operator.</h1>
        </div>

        {/* Stat cards */}
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<Users className="h-4 w-4" />}
            label="Total users"
            value={stats.users.total}
            sub={`${stats.users.verified} verified`}
            testid="stat-users"
          />
          <StatCard
            icon={<TrendingUp className="h-4 w-4" />}
            label="Last 7 days"
            value={stats.users.recent_signups_7d}
            sub="new signups"
            testid="stat-recent"
          />
          <StatCard
            icon={<Activity className="h-4 w-4" />}
            label="Active subscriptions"
            value={stats.subscriptions.active}
            sub={`${stats.subscriptions.pro_max} Pro Max`}
            testid="stat-active"
            highlight
          />
          <StatCard
            icon={<MessageSquare className="h-4 w-4" />}
            label="Total messages"
            value={stats.activity.total_messages}
            sub={`${stats.activity.total_conversations} conversations`}
            testid="stat-messages"
          />
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="codes" data-testid="tab-codes">Promo codes</TabsTrigger>
            <TabsTrigger value="apikeys" data-testid="tab-apikeys">API keys</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <LivePanel />
            <RevenuePanel />
            <div className="grid gap-5 lg:grid-cols-2">
              {/* Subscription distribution */}
              <Panel title="Subscription distribution" testid="subs-panel">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Standard", value: stats.subscriptions.standard },
                          { name: "Pro", value: stats.subscriptions.pro },
                          { name: "Pro Max", value: stats.subscriptions.pro_max },
                          { name: "Free", value: Math.max(0, stats.users.total - stats.subscriptions.standard - stats.subscriptions.pro - stats.subscriptions.pro_max) },
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={50}
                        paddingAngle={3}
                      >
                        {COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                      </Pie>
                      <RTooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  {[
                    ["Standard", stats.subscriptions.standard, COLORS[0]],
                    ["Pro", stats.subscriptions.pro, COLORS[1]],
                    ["Pro Max", stats.subscriptions.pro_max, COLORS[2]],
                  ].map(([label, val, color]) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                      <span className="text-muted-foreground">{label}:</span>
                      <span className="font-medium">{val}</span>
                    </div>
                  ))}
                </div>
              </Panel>

              {/* Top countries */}
              <Panel title="Top countries" testid="countries-panel">
                {stats.countries.length === 0 ? (
                  <div className="grid h-64 place-items-center text-sm text-muted-foreground">
                    No location data yet
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.countries.slice(0, 8)} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="country" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                        <RTooltip
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Panel title={`Users (${users.length})`} testid="users-panel">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-sm font-medium">{u.email}</TableCell>
                        <TableCell>
                          <PlanBadge plan={u.plan} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {u.country || "—"}
                        </TableCell>
                        <TableCell>
                          {u.is_verified ? (
                            <Badge variant="outline" className="border-green-500/40 text-green-600 dark:text-green-400">Yes</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Panel>
          </TabsContent>

          <TabsContent value="codes">
            <PromoCodesPanel codes={codes} onChange={loadAll} />
          </TabsContent>

          <TabsContent value="apikeys">
            <ApiKeysSection />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub, testid, highlight }) {
  return (
    <div
      className={`rounded-xl border p-5 transition ${
        highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card/50"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`grid h-7 w-7 place-items-center rounded-md ${highlight ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
          {icon}
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Panel({ title, children, testid, action }) {
  return (
    <div className="rounded-xl border border-border bg-card/50 p-5" data-testid={testid}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm font-semibold tracking-tight">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function PlanBadge({ plan }) {
  const map = {
    free: { label: "Free", Icon: null, color: "" },
    standard: { label: "Standard", Icon: Zap, color: "border-primary/30 text-primary" },
    pro: { label: "Pro", Icon: Crown, color: "border-primary/30 text-primary" },
    pro_max: { label: "Pro Max", Icon: Trophy, color: "bg-primary text-primary-foreground border-primary" },
  };
  const cfg = map[plan] || map.free;
  return (
    <Badge variant="outline" className={`gap-1 ${cfg.color}`}>
      {cfg.Icon && <cfg.Icon className="h-3 w-3" />} {cfg.label}
    </Badge>
  );
}

function PromoCodesPanel({ codes, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "",
    plan: "standard",
    duration_days: 30,
    max_uses: 1,
    note: "",
  });
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const handleCreate = async (e) => {
    e?.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        duration_days: Number(form.duration_days),
        max_uses: Number(form.max_uses),
      };
      if (!payload.code) delete payload.code;
      await adminCreateCode(payload);
      toast.success("Code created");
      setOpen(false);
      setForm({ code: "", plan: "standard", duration_days: 30, max_uses: 1, note: "" });
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this code?")) return;
    try {
      await adminDeleteCode(id);
      toast.success("Deleted");
      onChange();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleToggle = async (c) => {
    try {
      await adminToggleCode(c.id, !c.is_active);
      toast.success(c.is_active ? "Deactivated" : "Activated");
      onChange();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  // Filter + summary
  const filtered = (codes || []).filter((c) => {
    if (filterPlan !== "all" && c.plan !== filterPlan) return false;
    if (filterStatus === "active" && !c.is_active) return false;
    if (filterStatus === "inactive" && c.is_active) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${c.code} ${c.note || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const totalUses = (codes || []).reduce((acc, c) => acc + (c.current_uses || 0), 0);
  const activeCount = (codes || []).filter((c) => c.is_active).length;

  return (
    <Panel
      title={`Promo codes (${codes.length})`}
      testid="promo-codes-panel"
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-full" data-testid="create-code-btn">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New code
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-code-dialog">
            <DialogHeader>
              <DialogTitle>Generate promo code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Code (optional, auto-generates if blank)</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="SABZY-VIP"
                  className="uppercase tracking-widest"
                  data-testid="new-code-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="new-code-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard" data-testid="plan-option-standard">Standard (4.6)</SelectItem>
                    <SelectItem value="pro" data-testid="plan-option-pro">Pro (4.7)</SelectItem>
                    <SelectItem value="pro_max" data-testid="plan-option-pro_max">Pro Max (Racing)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Duration (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                    data-testid="new-code-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max uses (0 = unlimited)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    data-testid="new-code-max-uses-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Input
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Internal note"
                  data-testid="new-code-note-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} data-testid="submit-new-code-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <ScrollArea className="h-[520px]">
        {/* Summary row */}
        <div className="mb-3 grid grid-cols-3 gap-2 text-center" data-testid="promo-summary">
          <div className="rounded-lg border border-border/40 bg-card/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
            <div className="text-base font-bold tracking-tight">{codes.length}</div>
          </div>
          <div className="rounded-lg border border-border/40 bg-card/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</div>
            <div className="text-base font-bold tracking-tight text-emerald-500">{activeCount}</div>
          </div>
          <div className="rounded-lg border border-border/40 bg-card/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Redemptions</div>
            <div className="text-base font-bold tracking-tight">{totalUses}</div>
          </div>
        </div>

        {/* Search + filters */}
        <div className="mb-3 flex flex-wrap gap-2" data-testid="promo-filters">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code or note…"
            className="h-9 max-w-xs flex-1 text-xs"
            data-testid="promo-search-input"
          />
          <Select value={filterPlan} onValueChange={setFilterPlan}>
            <SelectTrigger className="h-9 w-32 text-xs" data-testid="promo-filter-plan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="pro_max">Pro Max</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-32 text-xs" data-testid="promo-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  {codes.length === 0 ? "No codes yet. Create one to get started." : "No codes match your filters."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`code-row-${c.id}`}>
                <TableCell>
                  <div className="flex flex-col">
                    <code className="rounded bg-muted px-2 py-1 text-xs font-bold tracking-widest" title={c.note || ""}>
                      {c.code}
                    </code>
                    {c.note && <span className="mt-0.5 text-[10px] text-muted-foreground line-clamp-1">{c.note}</span>}
                  </div>
                </TableCell>
                <TableCell><PlanBadge plan={c.plan} /></TableCell>
                <TableCell className="text-sm">
                  {c.current_uses}{c.max_uses > 0 ? ` / ${c.max_uses}` : " / ∞"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.duration_days}d</TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => handleToggle(c)}
                    className="cursor-pointer"
                    data-testid={`toggle-code-${c.id}`}
                    title={c.is_active ? "Click to deactivate" : "Click to activate"}
                  >
                    <Badge variant={c.is_active ? "outline" : "secondary"} className={c.is_active ? "border-emerald-500/40 text-emerald-500" : ""}>
                      {c.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => handleCopy(c.code)} data-testid={`copy-code-${c.id}`} title="Copy code">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-code-${c.id}`} title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </Panel>
  );
}



function RevenuePanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await adminRevenue();
        if (cancelled) return;
        setData(res.data);
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (err) {
    return (
      <Panel title="Revenue" testid="revenue-panel">
        <div className="py-6 text-center text-sm text-destructive">Could not load revenue data.</div>
      </Panel>
    );
  }
  if (!data) {
    return (
      <Panel title="Revenue" testid="revenue-panel">
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Crunching numbers…
        </div>
      </Panel>
    );
  }

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
          Revenue
          <span className="ml-1 text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
            via PayPal · refreshes every 15s
          </span>
        </span>
      }
      testid="revenue-panel"
    >
      {/* Top-line KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <RevenueKpi
          label="Lifetime revenue"
          value={fmt(data.lifetime_revenue)}
          sub={`${data.subscriptions.active} active · ${data.one_time.count} one-time`}
          accent="text-emerald-500"
          icon={DollarSign}
          testid="revenue-lifetime"
          big
        />
        <RevenueKpi
          label="MRR"
          value={fmt(data.mrr)}
          sub={`ARR ${fmt(data.arr)}`}
          icon={Repeat}
          testid="revenue-mrr"
        />
        <RevenueKpi
          label="One-time · 30d"
          value={fmt(data.one_time.last_30d)}
          sub={`${fmt(data.one_time.last_7d)} · last 7d`}
          icon={Receipt}
          testid="revenue-onetime-30d"
        />
        <RevenueKpi
          label="Today"
          value={fmt(data.one_time.today)}
          sub="captured today"
          icon={TrendingUp}
          testid="revenue-today"
        />
      </div>

      {/* Subscription breakdown by plan */}
      <div className="mt-5 rounded-xl border border-border/60 bg-secondary/30 p-4">
        <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Active subscriptions by tier</div>
        <div className="grid grid-cols-3 gap-3">
          {[
            ["Standard", data.subscriptions.by_plan?.standard || 0, "#C47A3A", 5],
            ["Pro", data.subscriptions.by_plan?.pro || 0, "#D98E4B", 12],
            ["Pro Max", data.subscriptions.by_plan?.pro_max || 0, "#E5A06A", 25],
          ].map(([label, count, color, price]) => (
            <div key={label} className="rounded-lg border border-border/40 bg-card p-3" data-testid={`revenue-sub-${label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                {label}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-bold tracking-tight">{count}</span>
                <span className="text-xs text-muted-foreground">subs</span>
              </div>
              <div className="text-[11px] text-muted-foreground">{fmt(count * price)} / mo</div>
            </div>
          ))}
        </div>
        {data.subscriptions.cancelled > 0 && (
          <div className="mt-3 text-[11px] text-muted-foreground">
            {data.subscriptions.cancelled} subscription{data.subscriptions.cancelled === 1 ? "" : "s"} cancelled
          </div>
        )}
      </div>

      {/* Recent activity feed */}
      <div className="mt-5">
        <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Recent transactions</div>
        {(!data.recent_activity || data.recent_activity.length === 0) ? (
          <div className="rounded-xl border border-dashed border-border/60 px-4 py-8 text-center text-sm text-muted-foreground" data-testid="revenue-no-activity">
            No transactions yet. Share your <code className="rounded bg-muted px-1.5 py-0.5">/upgrade</code> link to start earning.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border/60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-9 text-[11px]">When</TableHead>
                  <TableHead className="h-9 text-[11px]">Customer</TableHead>
                  <TableHead className="h-9 text-[11px]">Plan</TableHead>
                  <TableHead className="h-9 text-[11px]">Type</TableHead>
                  <TableHead className="h-9 text-right text-[11px]">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_activity.map((t, i) => (
                  <TableRow key={i} data-testid={`revenue-activity-row-${i}`}>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.at ? new Date(t.at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{t.email || "—"}</TableCell>
                    <TableCell><PlanBadge plan={t.plan} /></TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">
                        {t.kind === "subscription" ? "Subscription" : "One-time"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-500">
                      {fmt(t.amount_usd)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Panel>
  );
}


function RevenueKpi({ label, value, sub, accent = "", icon: Icon, testid, big }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className={`h-3.5 w-3.5 ${accent || "text-muted-foreground"}`} />}
      </div>
      <div className={`mt-2 font-bold tracking-tight ${big ? "text-3xl" : "text-2xl"} ${accent}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}


function LivePanel() {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await adminLiveStats();
        if (cancelled) return;
        setData(res.data);
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
      } catch (e) {
        console.warn("adminLiveStats poll failed:", e);
      }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data) {
    return (
      <Panel title="Live activity" testid="live-panel">
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      </Panel>
    );
  }

  const utilPct = data.utilisation?.online_vs_concurrent_pct ?? 0;
  const utilColor = utilPct > 80 ? "#ef4444" : utilPct > 50 ? "#f59e0b" : "#22c55e";

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full bg-emerald-500 ${pulse ? "animate-ping" : ""}`} />
          Live activity
          <span className="ml-1 text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
            refreshes every 8s
          </span>
        </span>
      }
      testid="live-panel"
    >
      <div className="grid gap-4 md:grid-cols-4">
        <LiveStat icon={Radio} label="Online now" value={data.online_now} accent="text-emerald-500" testid="live-online-now" sub="last 5 min" />
        <LiveStat icon={Activity} label="Active · 1h" value={data.active_1h} testid="live-active-1h" />
        <LiveStat icon={TrendingUp} label="Active · 24h" value={data.active_24h} testid="live-active-24h" />
        <LiveStat icon={MessageSquare} label="Msgs · 1h" value={data.messages_last_hour} testid="live-msgs-1h" />
      </div>

      {/* Capacity meter */}
      <div className="mt-6 rounded-xl border border-border/60 bg-secondary/30 p-4" data-testid="live-capacity">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Gauge className="h-4 w-4 text-primary" /> Server capacity
          </div>
          <span className="text-[11px] text-muted-foreground">
            Single-pod · 1 worker · free LLM tier
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-3 text-[13px]">
          <CapRow icon={Zap} label="Concurrent chat sessions" used={data.online_now} cap={data.capacity.concurrent_chat_sessions} testid="cap-concurrent" />
          <CapRow icon={Users} label="Authenticated browsing" used={data.active_1h} cap={data.capacity.concurrent_lightweight_users} testid="cap-browsing" />
          <CapRow icon={Server} label="Daily active (safe)" used={data.signups_today} cap={data.capacity.daily_active_users_safe} testid="cap-dau-safe" />
        </div>

        <div className="mt-4 rounded-lg bg-card/50 px-3 py-2 text-[12px] text-muted-foreground">
          Live utilisation: <span style={{ color: utilColor, fontWeight: 600 }}>{utilPct}%</span>{" "}
          · Hard ceiling on this infra: <b>{data.capacity.daily_active_users_max.toLocaleString()}</b> DAU{" "}
          · Estimated <b>{data.capacity.requests_per_second_estimate}</b> req/s
        </div>
      </div>

      {/* Online users list */}
      {data.online_users?.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Currently online ({data.online_users.length})
          </div>
          <div className="max-h-56 overflow-y-auto rounded-xl border border-border/60 bg-card/40">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead className="text-right">Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.online_users.map((u, i) => (
                  <TableRow key={i} data-testid={`online-user-${i}`}>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><PlanBadge plan={u.plan} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.country || "—"}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{relativeTime(u.last_active)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function LiveStat({ icon: Icon, label, value, accent = "text-foreground", sub, testid }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-3.5" data-testid={testid}>
      <div className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-2xl font-semibold ${accent}`}>{value ?? 0}</div>
      {sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function CapRow({ icon: Icon, label, used, cap, testid }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, cap)) * 100));
  const color = pct > 80 ? "bg-red-500" : pct > 50 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between text-[12px]">
        <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3 w-3" /> {label}</span>
        <span className="font-mono"><b className="text-foreground">{used}</b> / {cap}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary/60">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch {
    return "—";
  }
}
